import type { AppKind } from "common/components/app-kind";
import { formatLocalizedString, getDateString, getLocalizedString } from "common/components/localization";
import type { AppEnvironment, BasicUserProfile, RenderEnvironment } from "common/components/types";
import { getNowTimeZoneAgnostic, makeStartOfDayTimeZoneAgnostic } from "date-time/support";
import type { GlideFC } from "lib/glide-fc";
import type { LinkifyProps } from "linkifyjs/react";
import Linkify from "linkifyjs/react";
import * as React from "react";
import { DropdownMenu, DropdownMenuItem } from "sharedUI/ui/smart/dropdown-menu/dropdown-menu-style";
import { DropdownAnchor } from "sharedUI/ui/smart/dropdown-menu/dropdown-menu-types";
import { withTheme } from "styled-components";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import UserCircleImage from "../../views/user-circle-image/user-circle-image";
import {
    CommentAuthor,
    CommentContentArea,
    CommentDate,
    CommentText,
    CommentWrapper,
    DeleteButton,
    Header,
    SkeletonCommentDate,
    SkeletonCommentText,
    Wrapper,
} from "./comments-style";
import NewCommentBox from "./private/new-comment-box";

export interface CommentData {
    readonly username: string;
    readonly email: string | undefined;
    readonly dateTime: Date;
    readonly comment: string;
    readonly documentID: string;
}

interface Props {
    readonly comments: ReadonlyArray<CommentData>;
    readonly postComment: (username: string, email: string | undefined, comment: string) => void;
    readonly onTapNew?: () => void;
    readonly appEnvironment: AppEnvironment;
    readonly deleteComments?: (documentIDs: ReadonlyArray<string>) => void;
    readonly topic: string | undefined;
    // FIXME: Use this primarily to get user names and images
    readonly userProfiles: readonly BasicUserProfile[] | undefined;
    readonly renderEnvironment: RenderEnvironment;
}

interface CommentItemProps extends CommentData {
    onDelete?: () => void;
    image: string | undefined;
    appKind: AppKind;
}

interface CommentItemState {
    showDelete: boolean;
    isDeleting: boolean;
}

class CommentItem extends React.Component<CommentItemProps, CommentItemState> {
    public state: CommentItemState = {
        showDelete: false,
        isDeleting: false,
    };

    private onRightClick = (evnt: React.MouseEvent<HTMLDivElement>) => {
        if (this.props.onDelete === undefined) return;
        evnt.preventDefault();
        evnt.stopPropagation();

        this.setState({ showDelete: !this.state.showDelete });
    };

    private onDeleteClicked = () => {
        if (this.props.onDelete === undefined) return;
        this.props.onDelete();
        this.setState({ isDeleting: true, showDelete: false });
    };

    public render(): React.ReactNode {
        const { username, email, comment, dateTime, image, appKind } = this.props;

        let commentNode: React.ReactNode;
        const sections = comment.split("\n");
        if (sections.length === 0) {
            commentNode = comment;
        } else {
            commentNode = sections.map((s, i) => <TextWithLinks key={i}>{s}</TextWithLinks>);
        }

        const now = getNowTimeZoneAgnostic();
        const startOfDay = makeStartOfDayTimeZoneAgnostic(now);

        return (
            <CommentWrapper data-test="app-comment" onContextMenu={this.onRightClick}>
                <UserCircleImage size={38} username={username} email={email} image={image} aria-hidden={true} />
                <CommentContentArea>
                    <CommentText>
                        <CommentAuthor>{username}</CommentAuthor>
                        <CommentDate>{getDateString(appKind, dateTime, now, startOfDay, false)}</CommentDate>
                        {commentNode}
                    </CommentText>
                    <DropdownMenu isHidden={!this.state.showDelete} anchor={DropdownAnchor.BottomRight} minWidth={135}>
                        <DropdownMenuItem onClick={this.onDeleteClicked}>
                            <DeleteButton>Delete comment</DeleteButton>
                        </DropdownMenuItem>
                    </DropdownMenu>
                </CommentContentArea>
            </CommentWrapper>
        );
    }
}

interface TextWithLinksProps {
    children: React.ReactNode;
}

const options: LinkifyProps["options"] = {
    attributes: {
        rel: "noreferrer",
    },
    className: "comment-link",
    target: "_blank",
};

const TextWithLinks: GlideFC<TextWithLinksProps> = p => {
    return (
        <Linkify tagName="p" options={options}>
            {p.children}
        </Linkify>
    );
};

interface SkeletonProps {
    theme: RuntimeTheme;
}

class SkeletonComment extends React.Component<SkeletonProps> {
    public render(): React.ReactNode {
        return (
            <CommentWrapper>
                <UserCircleImage color={this.props.theme.skeletonColor} size={38} username={""} />
                <CommentContentArea>
                    <SkeletonCommentText />
                    <SkeletonCommentDate />
                </CommentContentArea>
            </CommentWrapper>
        );
    }
}

const ThemedSkeletonComment = withTheme(SkeletonComment);

interface State {
    deletedComments: string[];
}

export function CommentsSkeleton({ appKind }: { readonly appKind: AppKind }) {
    return (
        <Wrapper>
            <Header hasComments={true}>{getLocalizedString("loadingComments", appKind)}</Header>
            <ThemedSkeletonComment />
        </Wrapper>
    );
}

class Comments extends React.Component<Props, State> {
    public state: State = {
        deletedComments: [],
    };

    public componentDidMount() {
        const { topic, appEnvironment } = this.props;
        if (topic !== undefined) {
            appEnvironment.suppressNotificationTopic(topic);
        }
    }

    // It's possible for a topic to change in a Comments component, because the topic is
    // derived from the screen title, which is subject to change. When this happens, we
    // need to unsuppress the prior topic so that we still get notifications for it.
    public getSnapshotBeforeUpdate(prevProps: Props): null {
        const { topic: prevTopic, appEnvironment: prevAppEnvironment } = prevProps;
        if (prevTopic !== undefined) {
            prevAppEnvironment.unsuppressNotificationTopic(prevTopic);
        }
        const { topic, appEnvironment } = this.props;
        if (topic !== undefined) {
            appEnvironment.suppressNotificationTopic(topic);
        }
        return null;
    }

    // This keeps React nice and happy. It's only needed because getSnapshotBeforeUpdate's presence
    // offends the development tools if this isn't around as well.
    public componentDidUpdate() {
        return;
    }

    public componentWillUnmount() {
        const { topic, appEnvironment } = this.props;
        if (topic !== undefined) {
            appEnvironment.unsuppressNotificationTopic(topic);
        }
    }

    public render(): React.ReactNode {
        const { appKind } = this.props.appEnvironment;
        const comments = this.props.comments
            .filter(c => this.state.deletedComments.indexOf(c.documentID) === -1)
            .map((c, _i) => {
                let onDeleteComment: (() => void) | undefined;
                if (this.props.deleteComments !== undefined) {
                    onDeleteComment = () => {
                        if (this.props.deleteComments !== undefined) {
                            this.props.deleteComments([c.documentID]);
                        }
                        this.setState({ deletedComments: [...this.state.deletedComments, c.documentID] });
                    };
                }

                let username = c.username;
                let image: string | undefined;
                const profiles = this.props.userProfiles;
                if (profiles !== undefined) {
                    const profile = profiles.find(p => p.email === c.email);
                    if (profile !== undefined) {
                        username = profile.name ?? username;
                        image = profile.image;
                    }
                }

                return (
                    <CommentItem
                        key={c.dateTime.toString()}
                        {...c}
                        image={image}
                        username={username}
                        onDelete={onDeleteComment}
                        appKind={appKind}
                    />
                );
            });

        let header: string;
        if (comments.length === 0) {
            header = getLocalizedString("noComments", appKind);
        } else if (comments.length === 1) {
            header = getLocalizedString("oneComment", appKind);
        } else {
            header = formatLocalizedString("moreComments", [comments.length.toString()]);
        }

        return (
            <Wrapper data-test="app-comments-preview">
                <Header hasComments={comments.length > 0}>{header}</Header>
                {comments}
                <NewCommentBox
                    appEnvironment={this.props.appEnvironment}
                    onTapNew={this.props.onTapNew}
                    postComment={this.props.postComment}
                    renderEnvironment={this.props.renderEnvironment}
                />
            </Wrapper>
        );
    }
}

export default Comments;
